import { Box, TextField, Typography } from '@mui/material';
import * as React from 'react';
import { InsertLogRecordRequest, MobileBuildingDTO, MobileBuildingsVovClient, MobileRecordsVovClient } from '../../../ApiOld/Api/ApiServerVov';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { useEffect, useState } from 'react';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import LoadingIndicator from './LoadingIndicator';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import { prioritizedSearch } from './SearchUtil';

export interface IBuildingsListProps {
    onBuildingClick: (buildingId: MobileBuildingDTO | undefined) => void;
}

export default function BuildingsList(props: IBuildingsListProps) {
    const { onBuildingClick } = props;
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileBuildingsVovClient);
    const { authorizedMobileEmployeeApi: recordsApi } = useMobileApi(MobileRecordsVovClient);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { diagnosticAlerts, deviceModel, deviceId, versionWithBuild } = useEmployeeContext();
    const [buildings, setBuildings] = useState<MobileBuildingDTO[] | undefined>();
    const [filteredBuildings, setFilteredBuildings] = useState<MobileBuildingDTO[] | undefined>();
    const [searchTerm, setSearchTerm] = useState('');
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        authorizedMobileEmployeeApi.getAllBuildings().then((response) => {
            if (response) {
                setBuildings(response.buildings);
                setFilteredBuildings(response.buildings);
                setLoaded(true);
                if (diagnosticAlerts)
                    enqueueSnackbar(t('ProjectsLoadedSuccessfully'), { variant: 'success' });
            } else {
                setLoaded(true);
                enqueueSnackbar(t('NoProjectsWereFound'), { variant: 'error' });
            }
        }).catch((error) => {
            recordsApi.insertLog(new InsertLogRecordRequest({
                action: '(BuildingsList) Getting buildings catch ' + error.message,
                deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
            }));
            setLoaded(true);
            enqueueSnackbar(t('ThereWasAnErrorLoadingProjects'), { variant: 'error' });
        });
    }, [authorizedMobileEmployeeApi, deviceId, deviceModel, diagnosticAlerts, enqueueSnackbar, recordsApi, t, versionWithBuild]);

    useEffect(() => {
        if (buildings) {
            if (searchTerm) {
                const filtered = prioritizedSearch(
                    buildings,
                    searchTerm,
                    (building) => [building?.name || "", building?.code || ""]
                );
                setFilteredBuildings(filtered);
            } else {
                setFilteredBuildings(buildings);
            }
        }
    }, [searchTerm, buildings]);

    if (!loaded) {
        return <LoadingIndicator />;
    }

    return (
        <Box sx={{ m: 2 }}>
            <TextField
                fullWidth
                size="small"
                sx={{ boxShadow: 2, borderRadius: 4, mb: 2 }}
                placeholder={t('Search...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    endAdornment: <SearchOutlinedIcon />,
                }}
            />
            {filteredBuildings?.length !== 0 && <Box sx={{ p: 2, boxShadow: 2, borderRadius: 4 }}>
                {filteredBuildings?.map((building, index) => (
                    <React.Fragment key={building.buildingId}>
                        <Box
                            onClick={() => onBuildingClick(building)}
                            sx={{ width: '100%' }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 1 }}>
                                <DomainOutlinedIcon sx={{ color: '#FFC600' }} />
                                <Typography sx={{ fontWeight: 'bold' }}>{building.code}</Typography>
                            </Box>
                            <Typography sx={{ fontWeight: 'bold' }}>{building.name}</Typography>
                        </Box>
                        {index < filteredBuildings.length - 1 && (
                            <Box sx={{ my: 2, borderBottom: '1px solid #ccc' }} />
                        )}
                    </React.Fragment>
                ))}
            </Box>
            }
        </Box>
    );
}
